import {
  Inbox,
  LayoutGrid,
  Vote,
  Activity,
  Bell,
  Building2,
  Building2 as BuildingPlus,
  Globe,
  UserRound,
  CircleDollarSign,
  CreditCard,
  Home,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  MessageCircle,
  PiggyBank,
  Receipt,
  ShieldCheck,
  LayoutPanelTop,
  UserCog,
  Users,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';
import type { Role } from '@/api/types';

export type NavIconName =
  | 'Dashboard'
  | 'GridView'
  | 'Payments'
  | 'ReceiptLong'
  | 'Handyman'
  | 'Public'
  | 'Home'
  | 'Fund'
  | 'Announcements'
  | 'Inquiries'
  | 'Notifications'
  | 'Users'
  | 'Reports'
  | 'Console'
  | 'Team'
  | 'Billing'
  | 'Support'
  | 'Account'
  | 'Site'
  | 'AddBuilding'
  | 'Pulse'
  | 'Services'
  | 'Vote'
  | 'Inbox';

export const navIcons: Record<NavIconName, LucideIcon> = {
  Dashboard: LayoutDashboard,
  GridView: Building2,
  Payments: CircleDollarSign,
  ReceiptLong: Receipt,
  Handyman: LifeBuoy,
  Public: ShieldCheck,
  Home,
  Fund: PiggyBank,
  Announcements: Megaphone,
  Inquiries: MessageCircle,
  Notifications: Bell,
  Users,
  Reports: WalletCards,
  Console: LayoutPanelTop,
  Team: UserCog,
  Billing: CreditCard,
  Support: LifeBuoy,
  Account: UserRound,
  Site: Globe,
  AddBuilding: BuildingPlus,
  Pulse: Activity,
  Services: LayoutGrid,
  Vote: Vote,
  Inbox: Inbox,
};

export type NavItem = {
  to: string;
  labelKey: string;
  icon: NavIconName;
  roles?: Role[];
  section?: 'platform' | 'main' | 'comms';
  hidden?: boolean;
  badge?: 'lateUnits' | 'openTickets';
};

export const navItems: NavItem[] = [
  // Platform team only — never rendered for a manager or resident.
  // المحرّر يرى العمارات والمستخدمين والإنشاء — لا التسعير ولا الهوية.
  { to: '/admin', labelKey: 'nav.console', icon: 'Console', roles: ['admin', 'editor'], section: 'platform' },
  { to: '/admin/buildings', labelKey: 'nav.allBuildings', icon: 'GridView', roles: ['admin', 'editor'], section: 'platform' },
  { to: '/admin/onboard', labelKey: 'nav.onboard', icon: 'AddBuilding', roles: ['admin', 'editor'], section: 'platform' },
  { to: '/admin/users', labelKey: 'nav.allUsers', icon: 'Team', roles: ['admin', 'editor'], section: 'platform' },
  { to: '/admin/billing', labelKey: 'nav.billing', icon: 'Billing', roles: ['admin'], section: 'platform' },
  { to: '/admin/site', labelKey: 'nav.site', icon: 'Site', roles: ['admin'], section: 'platform' },
  /* صفحة صندوق الوارد كانت مبنية بلا رابط — ثالث رابط ميّت من نوعه بعد
     `/users` و`/notifications`. أي صفحة تُضاف يجب أن يُضاف بابها معها. */
  { to: '/admin/inbox', labelKey: 'nav.inbox', icon: 'Inbox', roles: ['admin'], section: 'platform' },
  { to: '/admin/observability', labelKey: 'nav.observability', icon: 'Pulse', roles: ['admin'], section: 'platform' },

  { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'Dashboard', roles: ['admin', 'manager'] },
  // `/me` لا `/home`: الأخيرة الصفحة التعريفية العامة (#46).
  { to: '/me', labelKey: 'nav.home', icon: 'Home', roles: ['resident'] },
  { to: '/units', labelKey: 'nav.units', icon: 'GridView', roles: ['admin', 'manager'] },
  { to: '/payments', labelKey: 'nav.payments', icon: 'Payments', badge: 'lateUnits' },
  { to: '/expenses', labelKey: 'nav.expenses', icon: 'ReceiptLong', roles: ['admin', 'manager'] },
  // #137: دفتر المصاريف للساكن — الصفحة نفسها تعرض رسالة إن أطفأه المسؤول.
  { to: '/expenses-ledger', labelKey: 'nav.expensesLedger', icon: 'ReceiptLong', roles: ['resident'] },
  { to: '/tickets', labelKey: 'nav.tickets', icon: 'Handyman', badge: 'openTickets' },
  /*
    بوّابة واحدة بدل عشرة روابط.
    -------------------------
    الشريط الجانبي يتحمّل خمسة أو ستة عناصر قبل أن يصير قائمةً تُمسح
    بالعين ولا تُقرأ. وما يُفتح يومياً — النظرة العامة والتحصيل والوحدات
    والمصروفات والصيانة — يبقى هنا، وما يُفتح مرة في الشهر يسكن صفحة
    الخدمات: مربّعات مرتّبة وبحث، لا قائمة تطول كلّما أُضيفت ميزة.
  */
  { to: '/polls', labelKey: 'nav.polls', icon: 'Vote', section: 'comms' },
  // #51: للجميع — الساكن يقرأ، والمسؤول يرسل. كانت مخفية ومحصورة بالمسؤول.
  { to: '/announcements', labelKey: 'nav.announcements', icon: 'Announcements', section: 'comms' },
  { to: '/services', labelKey: 'nav.services', icon: 'Services', roles: ['admin', 'manager'] },
  { to: '/transparency', labelKey: 'nav.transparency', icon: 'Public', section: 'comms' , hidden: true},

  // Later modules — flip `hidden` to false once the pages exist.
  { to: '/fund', labelKey: 'nav.fund', icon: 'Fund', roles: ['admin', 'manager'] , hidden: true},
  { to: '/invoices', labelKey: 'nav.invoices', icon: 'Billing', roles: ['admin', 'manager'] , hidden: true},
  { to: '/support', labelKey: 'nav.support', icon: 'Support', section: 'comms' , hidden: true},
  { to: '/account', labelKey: 'nav.account', icon: 'Account', section: 'comms' },
  { to: '/inquiries', labelKey: 'nav.inquiries', icon: 'Inquiries', section: 'comms', hidden: true },
  { to: '/notifications', labelKey: 'nav.notifications', icon: 'Notifications', section: 'comms', hidden: true },
  { to: '/users', labelKey: 'nav.users', icon: 'Users', roles: ['admin', 'manager'] , hidden: true},
  // #90: لا مسار `/reports` في التطبيق — البند كان يقود إلى «الصفحة غير موجودة».
  // تقارير العمارة داخل لوحة النظرة العامة (المجاميع منذ البداية)، وتقرير
  // التحصيل في لوحة المنصّة. أُزيل البند بدل أن يبقى باباً على حائط.
];

export function visibleNavItems(roles: Role[]): NavItem[] {
  return navItems.filter(
    (item) => !item.hidden && (!item.roles || item.roles.some((role) => roles.includes(role))),
  );
}

/** Mobile thumb-zone tabs (bible §8). */
export const bottomNavRoutes: { to: string; labelKey: string; icon: NavIconName; roles?: Role[] }[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'Dashboard', roles: ['manager'] },
  { to: '/me', labelKey: 'nav.home', icon: 'Home', roles: ['resident'] },
  { to: '/payments', labelKey: 'nav.payments', icon: 'Payments' },
  { to: '/tickets', labelKey: 'nav.tickets', icon: 'Handyman' },
  { to: '/units', labelKey: 'nav.units', icon: 'GridView', roles: ['admin', 'manager'] },
];

